/**
 * The arithmetic the invoice screens share, in one React-free place.
 *
 * Small on purpose. What an invoice IS - its status, whether a card payment is
 * on offer - is the server's answer and is never worked out here. This file
 * only does what a reader does in their head with the columns already on
 * screen: subtract what was paid from what was charged, and add a column up.
 */
import type { PortalInvoice } from "../../../lib/portal";

type Money = Pick<PortalInvoice, "totalCents" | "paidCents">;
type Row = Pick<PortalInvoice, "status" | "totalCents" | "paidCents" | "currency">;

/** The statuses a client can still owe money on. A draft never reaches them. */
const OPEN_STATUSES = new Set(["sent", "partially_paid", "overdue"]);

/** What is still owed on one invoice. Never negative: an overpayment is not a debt. */
export function outstandingCents(inv: Money): number {
  return Math.max(0, inv.totalCents - inv.paidCents);
}

/** An invoice the client still has to pay something on. */
export function isUnpaid(inv: Row): boolean {
  return OPEN_STATUSES.has(inv.status) && outstandingCents(inv) > 0;
}

/**
 * Past its due date, by the SERVER's word.
 *
 * Read from the stored status and not from the date beside it. The front page's
 * own count of overdue invoices comes from that status, and a browser that
 * decided for itself would let the list and the number above it disagree for
 * the hours between a deadline passing and the status following it.
 */
export function isOverdue(inv: Row): boolean {
  return inv.status === "overdue" && outstandingCents(inv) > 0;
}

export type InvoiceFilter = "all" | "open" | "paid";

export function filterInvoices<T extends Row>(list: readonly T[], filter: InvoiceFilter): T[] {
  if (filter === "open") return list.filter(isUnpaid);
  if (filter === "paid") return list.filter((inv) => inv.status === "paid");
  return [...list];
}

export interface InvoiceTotals {
  outstandingCents: number;
  overdueCents: number;
  overdueCount: number;
  openCount: number;
  paidCount: number;
  /** The currency the sums are printed in: the first invoice's, else EUR. */
  currency: string;
}

/** The figures above the invoice list, and the counts on its filter. */
export function invoiceTotals(list: readonly Row[]): InvoiceTotals {
  const totals: InvoiceTotals = {
    outstandingCents: 0,
    overdueCents: 0,
    overdueCount: 0,
    openCount: 0,
    paidCount: 0,
    currency: list[0]?.currency ?? "EUR",
  };
  for (const inv of list) {
    if (inv.status === "paid") totals.paidCount += 1;
    if (!isUnpaid(inv)) continue;
    const owed = outstandingCents(inv);
    totals.openCount += 1;
    totals.outstandingCents += owed;
    if (isOverdue(inv)) {
      totals.overdueCount += 1;
      totals.overdueCents += owed;
    }
  }
  return totals;
}
